import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Spinner } from 'flowbite-react';
import * as sched from 'sched';
import { useAuth } from '../hooks/useAuth';
import { useWebSocket, WebSocketCallbacks, CommandBatch } from '../hooks/useWebSocket';
import { FiCopy, FiChevronDown } from 'react-icons/fi';
import ClearLocalCacheButton from '../components/common/ClearLocalCacheButton';
import { useAsync } from 'react-async-hook';

/** Callback for receiving conversation messages via WebSocket. */
export type ConversationMessageListener = (message: unknown) => void;
/** Callback for receiving conversation status changes via WebSocket. */
export type ConversationStatusListener = (conversationId: string, status: { type: string }) => void;

// Define the context type
interface WorkspaceContextType {
  safeData: sched.SafeData;
  workspaceId: string;
  getUserProfile: (userId: string) => Promise<sched.UserProfile | undefined>;
  /** Whether the WebSocket connection is established. */
  wsConnected: boolean;
  /** Submit CQRS commands via WebSocket. Returns correlation_id. */
  submitCommands: (commands: unknown[]) => string;
  /** The currently in-flight command batch, if any. */
  inFlightBatch: CommandBatch | null;
  /** Dismiss a failed batch to unblock the queue. */
  dismissFailedBatch: (correlationId: string) => void;
  /** Subscribe to a conversation's WebSocket events. Returns unsubscribe function. */
  subscribeConversation: (
    conversationId: string,
    onMessage: ConversationMessageListener,
    onStatus: ConversationStatusListener,
  ) => () => void;
}

// Create the context with default values
export const WorkspaceContext = createContext<WorkspaceContextType>(undefined!);

// Hook to use the workspace context
export const useWorkspace = () => useContext(WorkspaceContext);

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { client, user, token } = useAuth();
  const [safeData, setSafeData] = useState<sched.SafeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serializedDatabase, setSerializedDatabase] = useState<string | null>(null);
  const [missingUserProfileIds, setMissingUserProfileIds] = useState<string[]>([]);

  // Keep safeData in a ref so WebSocket callbacks can access the latest value
  // without causing the callbacks (and thus the WS connection) to be recreated.
  const safeDataRef = React.useRef<sched.SafeData | null>(null);
  safeDataRef.current = safeData;

  // ── Conversation listener registry ────────────────────────────
  // Consumers (useConversation) register listeners for their active conversation.
  // The WebSocket callbacks dispatch to all registered listeners.
  const conversationListenersRef = React.useRef<
    Map<string, { onMessage: ConversationMessageListener; onStatus: ConversationStatusListener }>
  >(new Map());

  const subscribeConversation = useCallback(
    (
      conversationId: string,
      onMessage: ConversationMessageListener,
      onStatus: ConversationStatusListener,
    ) => {
      conversationListenersRef.current.set(conversationId, { onMessage, onStatus });
      // Subscribe to the conversation entity over WebSocket
      wsSubscribeRef.current?.(conversationId);
      return () => {
        conversationListenersRef.current.delete(conversationId);
        wsUnsubscribeRef.current?.(conversationId);
      };
    },
    [],
  );

  // Refs for WS subscribe/unsubscribe so subscribeConversation doesn't depend on them
  const wsSubscribeRef = React.useRef<((entityId: string, afterEventId?: number) => void) | null>(null);
  const wsUnsubscribeRef = React.useRef<((entityId: string) => void) | null>(null);

  // ── Auto-save: track correlation_id → pending_change_ids ──────
  // When a batch is confirmed, we clear the corresponding pending changes from WASM.
  const batchPendingIdsRef = React.useRef<Map<string, string[]>>(new Map());
  // All pending change IDs that are currently claimed by in-flight or queued batches.
  // These are excluded from subsequent flushes to prevent double-sending.
  const claimedIdsRef = React.useRef<Set<string>>(new Set());

  // ── WebSocket callbacks (stable refs — don't depend on safeData) ──
  const wsCallbacks: WebSocketCallbacks = React.useMemo(
    () => ({
      onCqrsEvents: async (_workspaceId, events, correlationId) => {
        const sd = safeDataRef.current;
        if (!sd) return;
        try {
          const eventsJson = JSON.stringify(events);
          await sched.DataManager.apply_remote_events_from_json(sd, eventsJson);
        } catch (err) {
          console.error('[ws] failed to apply CQRS events:', err);
        }

        // If this confirms a batch we sent, clear those pending changes
        if (correlationId) {
          const pendingIds = batchPendingIdsRef.current.get(correlationId);
          if (pendingIds && sd) {
            try {
              await sched.DataManager.remove_confirmed_pending_changes(
                sd,
                JSON.stringify(pendingIds),
              );
            } catch (err) {
              console.error('[ws] failed to clear confirmed pending changes:', err);
            }
            // Release claimed IDs
            for (const id of pendingIds) {
              claimedIdsRef.current.delete(id);
            }
            batchPendingIdsRef.current.delete(correlationId);
          }
        }
      },
      onConversationMessage: (message: unknown) => {
        // Dispatch to the listener for this conversation
        const convId = (message as any)?.conversation_id;
        if (convId) {
          conversationListenersRef.current.get(convId)?.onMessage(message);
        }
      },
      onConversationStatusChange: (conversationId: string, status: { type: string }) => {
        conversationListenersRef.current.get(conversationId)?.onStatus(conversationId, status);
      },
      onCommandError: (correlationId, error) => {
        console.error(`[ws] command error (${correlationId}):`, error);
        // Release claimed IDs so the pending changes remain and can be
        // retried or undone by the user.
        const pendingIds = batchPendingIdsRef.current.get(correlationId);
        if (pendingIds) {
          for (const id of pendingIds) {
            claimedIdsRef.current.delete(id);
          }
          batchPendingIdsRef.current.delete(correlationId);
        }
      },
    }),
    [],
  );

  const {
    connected: wsConnected,
    subscribe: wsSubscribe,
    unsubscribe: wsUnsubscribe,
    submitCommands: wsSubmitCommands,
    inFlightBatch,
    dismissFailedBatch,
  } = useWebSocket(wsCallbacks);

  // Update refs so subscribeConversation can use them without dep cycles
  wsSubscribeRef.current = wsSubscribe;
  wsUnsubscribeRef.current = wsUnsubscribe;

  // Wrap submitCommands to inject workspaceId
  const submitCommands = useCallback(
    (commands: unknown[]) => {
      if (!workspaceId) throw new Error('no workspaceId');
      return wsSubmitCommands(workspaceId, commands);
    },
    [workspaceId, wsSubmitCommands],
  );

  // ── Auto-save: flush pending changes to server via WebSocket ────
  // Subscribes to PendingChanges notifications from WASM. When new pending
  // changes appear, sends them as a command batch if the channel is clear.
  const flushingRef = React.useRef(false);

  const flushPendingChanges = useCallback(async () => {
    const sd = safeDataRef.current;
    if (!sd || !workspaceId) {
      console.log('[auto-save] skip: no safeData or workspaceId');
      return;
    }
    if (!wsConnected) {
      console.log('[auto-save] skip: WebSocket not connected');
      return;
    }
    if (flushingRef.current) {
      console.log('[auto-save] skip: already flushing');
      return;
    }
    if (inFlightBatch) {
      console.log('[auto-save] skip: batch in flight');
      return;
    }

    flushingRef.current = true;
    try {
      // Get commands that aren't already claimed by an in-flight/queued batch
      const excludeJson = JSON.stringify([...claimedIdsRef.current]);
      const payloadJson = await sched.DataManager.get_unsent_pending_commands_as_json(
        sd,
        excludeJson,
      );
      if (!payloadJson) return; // Nothing new to send

      const payload = JSON.parse(payloadJson);
      const commands: unknown[] = payload.commands;
      const pendingChangeIds: string[] = payload.pending_change_ids;

      if (commands.length === 0) return;
      console.log(`[auto-save] flushing ${commands.length} commands, ids=${pendingChangeIds.join(',').substring(0, 60)}...`);

      // Claim these IDs so they aren't re-sent
      for (const id of pendingChangeIds) {
        claimedIdsRef.current.add(id);
      }

      const correlationId = wsSubmitCommands(workspaceId, commands);
      batchPendingIdsRef.current.set(correlationId, pendingChangeIds);
    } catch (err) {
      console.error('[auto-save] flush failed:', err);
    } finally {
      flushingRef.current = false;
    }
  }, [workspaceId, wsConnected, inFlightBatch, wsSubmitCommands]);

  // Ref to always call the latest flushPendingChanges without re-subscribing
  const flushRef = React.useRef(flushPendingChanges);
  flushRef.current = flushPendingChanges;

  // Subscribe to PendingChanges ONCE when safeData is available.
  // The callback uses flushRef so it always calls the current version.
  useEffect(() => {
    if (!safeData) return;

    console.log('[auto-save] subscribing to pending changes');

    const callback = (newPendingChanges: unknown) => {
      const count = Array.isArray(newPendingChanges) ? newPendingChanges.length : '?';
      console.log(`[auto-save] pending changes updated (${count} items)`);
      setTimeout(() => flushRef.current(), 0);
    };
    const jsCallback = callback as unknown as Function;

    sched.DataManager.select_pending_changes(safeData, jsCallback)
      .then(() => console.log('[auto-save] subscribed successfully'))
      .catch((err: any) => console.error('[auto-save] subscribe failed:', err));

    return () => {
      if ((safeData as any).__wbg_ptr !== 0) {
        sched.DataManager.unsubscribe(safeData, jsCallback)
          .catch((err: any) => console.error('Error unsubscribing:', err));
      }
    };
  }, [safeData]); // Only depends on safeData — stable subscription

  // Also flush when connection establishes or a batch completes
  useEffect(() => {
    if (!inFlightBatch && wsConnected) {
      flushPendingChanges();
    }
  }, [inFlightBatch, wsConnected, flushPendingChanges]);

  const listUserProfilesResponse = useAsync(async () => {
    if (!client || !token || !workspaceId) return [];

    return (await sched.WrappedApiClient.list_workspace_user_profiles(client, token, workspaceId)).user_profiles;
  }, [
    token, workspaceId
  ]);

  useEffect(() => {
    const loadWorkspace = async () => {
      if (!workspaceId || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get the authentication token
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        if (!token) {
          throw new Error('No authentication token available');
        }

        // Load the data manager
        const manager = await sched.SafeData.load(client, workspaceId, token);
        setSafeData(manager);

        setLoading(false);
      } catch (err) {
        console.error('Error loading workspace:', err);
        setError(err instanceof Error ? err.message : 'Failed to load workspace');

        const serializedDatabase = await sched.DataManager.load_database_from_storage_but_do_not_deserialize_yet(workspaceId);
        setSerializedDatabase(serializedDatabase);

        setLoading(false);
      }
    };

    loadWorkspace();
    // Deliberately does not re-run on token change
  }, [workspaceId]);

  // Subscribe to workspace events via WebSocket once data is loaded
  useEffect(() => {
    if (!safeData || !workspaceId || !wsConnected) return;

    (async () => {
      const lastEventId = await sched.DataManager.get_last_remote_event_id(safeData);
      wsSubscribe(workspaceId, lastEventId ?? undefined);
    })();
  }, [safeData, workspaceId, wsConnected, wsSubscribe]);

  // Return the cached user profile, if available. If not, refresh the query
  const getUserProfile = useCallback(async (userId: string) => {
    const profile = listUserProfilesResponse.result?.find((profile) => profile.id === userId);
    if (!profile && !missingUserProfileIds.includes(userId)) {
      setMissingUserProfileIds([...missingUserProfileIds, userId]);
      await listUserProfilesResponse.execute();
      return listUserProfilesResponse.result?.find((profile) => profile.id === userId);
    }
    return profile;
  }, [listUserProfilesResponse.result, missingUserProfileIds]);

  // Show loading spinner while data is being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Spinner size="xl" aria-label="Loading workspace" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Show error message if loading failed
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Error Loading Workspace</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">{error}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
            <ClearLocalCacheButton workspaceId={workspaceId} />
          </div>
          {serializedDatabase && (
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">Database Debug Information</h4>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg">
                    <span className="font-medium">Database Debug View</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (serializedDatabase) {
                            navigator.clipboard.writeText(JSON.stringify(serializedDatabase, null, 2));
                            // Optional: Add a visual feedback like a tooltip or temporary text
                          }
                        }}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Copy to clipboard"
                      >
                        <FiCopy className="w-5 h-5" />
                      </button>
                      <span className="transition group-open:rotate-180">
                        <FiChevronDown className="w-5 h-5" />
                      </span>
                    </div>
                  </summary>
                  <div className="p-4 bg-white dark:bg-gray-900">
                    <div className="max-h-96 overflow-auto">
                      <pre className="text-xs whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-4 rounded">
                        {JSON.stringify(serializedDatabase, null, 2)}
                      </pre>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If no workspace ID is provided, show a message
  if (!workspaceId || !safeData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Workspace Selected</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">Please select a workspace to continue.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <WorkspaceContext.Provider value={{
      safeData,
      workspaceId,
      getUserProfile,
      wsConnected,
      submitCommands,
      inFlightBatch,
      dismissFailedBatch,
      subscribeConversation,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
